import {
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears
} from "date-fns";

import { endOfLocalDayInUTC, getUserTimezone, localToUTC, startOfLocalDayInUTC } from "./timezone";

export type TimeRange =
  | "allTime"
  | "today"
  | "yesterday"
  | "last7Days"
  | "last30Days"
  | "last90Days"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth"
  | "last3Months"
  | "thisYear"
  | "lastYear"
  | string; // Support custom date ranges in format custom:YYYY-MM-DD:YYYY-MM-DD

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface DateRangeFormatted {
  from: string;
  to: string;
}

// Time range calculator functions
const calculateAllTime = (): DateRange => ({
  startDate: new Date(0).toISOString(), // January 1, 1970
  endDate: new Date().toISOString() // Current time
});

const calculateToday = (date: Date, tz: string): DateRange => ({
  startDate: startOfLocalDayInUTC(date, tz),
  endDate: endOfLocalDayInUTC(date, tz)
});

const calculateYesterday = (date: Date, tz: string): DateRange => {
  const yesterday = subDays(date, 1);
  const result = {
    startDate: startOfLocalDayInUTC(yesterday, tz),
    endDate: endOfLocalDayInUTC(yesterday, tz)
  };
  return result;
};

const calculateThisWeek = (date: Date, tz: string): DateRange => {
  const weekStart = startOfWeek(date);
  const weekEnd = endOfWeek(date);
  return {
    startDate: localToUTC(weekStart, tz),
    endDate: localToUTC(weekEnd, tz)
  };
};

const calculateLastWeek = (date: Date, tz: string): DateRange => {
  const lastWeekStart = subWeeks(startOfWeek(date), 1);
  const lastWeekEnd = subWeeks(endOfWeek(date), 1);
  return {
    startDate: localToUTC(lastWeekStart, tz),
    endDate: localToUTC(lastWeekEnd, tz)
  };
};

const calculateThisMonth = (date: Date, tz: string): DateRange => {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  return {
    startDate: localToUTC(monthStart, tz),
    endDate: localToUTC(monthEnd, tz)
  };
};

const calculateLastMonth = (date: Date, tz: string): DateRange => {
  const lastMonth = subMonths(date, 1);
  return {
    startDate: localToUTC(startOfMonth(lastMonth), tz),
    endDate: localToUTC(endOfMonth(lastMonth), tz)
  };
};

const calculateLast3Months = (date: Date, tz: string): DateRange => {
  const threeMonthsAgo = subMonths(date, 3);
  return {
    startDate: localToUTC(startOfMonth(threeMonthsAgo), tz),
    endDate: localToUTC(endOfMonth(date), tz)
  };
};

const calculateThisYear = (date: Date, tz: string): DateRange => {
  const yearStart = startOfYear(date);
  const yearEnd = endOfYear(date);
  return {
    startDate: localToUTC(yearStart, tz),
    endDate: localToUTC(yearEnd, tz)
  };
};

const calculateLastYear = (date: Date, tz: string): DateRange => {
  const lastYear = subYears(date, 1);
  return {
    startDate: localToUTC(startOfYear(lastYear), tz),
    endDate: localToUTC(endOfYear(lastYear), tz)
  };
};

const calculateLast7Days = (date: Date, tz: string): DateRange => {
  const sevenDaysAgo = subDays(date, 6); // Changed from 7 to 6 to include today
  return {
    startDate: startOfLocalDayInUTC(sevenDaysAgo, tz),
    endDate: endOfLocalDayInUTC(date, tz)
  };
};

const calculateLast30Days = (date: Date, tz: string): DateRange => {
  const thirtyDaysAgo = subDays(date, 29); // Changed from 30 to 29 to include today
  return {
    startDate: startOfLocalDayInUTC(thirtyDaysAgo, tz),
    endDate: endOfLocalDayInUTC(date, tz)
  };
};

const calculateLast90Days = (date: Date, tz: string): DateRange => {
  const ninetyDaysAgo = subDays(date, 89);
  return {
    startDate: startOfLocalDayInUTC(ninetyDaysAgo, tz),
    endDate: endOfLocalDayInUTC(date, tz)
  };
};

// Map of time range calculators
const timeRangeCalculators: Record<TimeRange, (date: Date, tz: string) => DateRange> = {
  allTime: () => calculateAllTime(),
  today: calculateToday,
  yesterday: calculateYesterday,
  last7Days: calculateLast7Days,
  last30Days: calculateLast30Days,
  last90Days: calculateLast90Days,
  thisWeek: calculateThisWeek,
  lastWeek: calculateLastWeek,
  thisMonth: calculateThisMonth,
  lastMonth: calculateLastMonth,
  last3Months: calculateLast3Months,
  thisYear: calculateThisYear,
  lastYear: calculateLastYear
};

// Get date range in UTC format for database queries
export const getTimeRangeFilter = (timeRange: TimeRange | string, timezone: string = getUserTimezone()): DateRange => {
  // Check if it's a custom date range format: custom:YYYY-MM-DD:YYYY-MM-DD
  if (typeof timeRange === "string" && timeRange.startsWith("custom:")) {
    const parts = timeRange.split(":");
    if (parts.length === 3) {
      const startDate = new Date(parts[1] + "T00:00:00");
      const endDate = new Date(parts[2] + "T23:59:59");
      return {
        startDate: localToUTC(startDate, timezone),
        endDate: localToUTC(endDate, timezone)
      };
    }
  }

  const now = new Date();
  const calculator = timeRangeCalculators[timeRange as TimeRange] || timeRangeCalculators.thisWeek;
  return calculator(now, timezone);
};

// Convert time range to database filters
export const getTimeRangeDbFilters = (timeRange: TimeRange | string, timezone: string = getUserTimezone()): { created_from?: string; created_to?: string } => {
  const { startDate, endDate } = getTimeRangeFilter(timeRange, timezone);
  return {
    created_from: startDate,
    created_to: endDate
  };
};

export const TIME_RANGE_LABELS: Record<string, string> = {
  today: "Today",
  yesterday: "Yesterday",
  thisWeek: "This Week",
  lastWeek: "Last Week",
  last7Days: "Last 7 Days",
  thisMonth: "This Month",
  lastMonth: "Last Month",
  last30Days: "Last 30 Days",
  last90Days: "Last 90 Days",
  last3Months: "Last 3 Months",
  thisYear: "This Year",
  lastYear: "Last Year",
  allTime: "All Time"
};

export const TIME_RANGES = Object.keys(TIME_RANGE_LABELS) as TimeRange[];

export const getTimeRangeLabel = (timeRange: string): string => {
  if (timeRange.startsWith("custom:")) {
    const parts = timeRange.split(":");
    if (parts.length === 3) {
      return `${parts[1]} to ${parts[2]}`;
    }
  }
  return TIME_RANGE_LABELS[timeRange] || timeRange;
};
